import moment from "moment";
import invariant from "ts-invariant";
import { useCallback, useMemo, useState } from "react";
import {
  EyeOutlined,
  DeliveredProcedureOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { Button, Col, Divider, Row, Space, Table } from "antd";

import nodataImgUrl from "@/assets/nodata.png";

import { FullLoading } from "@/components/FullLoading";
import { formatBalance } from "@/utils/formatters";
import { useSearchParam } from "@/utils/url-state";
import { OrderType, PurchaseDataFragment } from "@/graphql";

import { HistoryModal } from "../../../balance/detail/historyModal";
import { useTabBalanceHistory } from "./useTabBalanceHistory";
import ExcelJS from "exceljs";
import { useBalanceHistoryExcel } from "./useBalanceHistoryExcel";
import { useReleaseHold } from "./useReleaseHold";
import { assert } from "console";
import { useCanEdit } from "@/hooks/usePermission";

const columns = [
  { header: "Өмнөх үлдэгдэл", key: "beforeBalance" },
  { header: "Захиалгын дүн", key: "amount" },
  { header: "Дараах үлдэгдэл", key: "afterBalance" },
  { header: "Огноо", key: "createdAt" },
  { header: "Гүйлгээний утга", key: "description" },
  { header: "Төлөв", key: "status" },
];

export const BalanceHistoryTab = ({ _id }: { _id: string }) => {
  const canEdit = useCanEdit("users-list");
  const { loading, data, error } = useTabBalanceHistory({ _id });

  const [purchaseId, { set, unset }] = useSearchParam("purchase");

  const { loading: historyLoading, downloadBalanceExcel } =
    useBalanceHistoryExcel({ data: data?.balanceHistory ?? [] });

  const {
    loading: holdLoading,
    releaseHold,
    currentPurchaseId,
  } = useReleaseHold();

  const activePurchase = useMemo<PurchaseDataFragment | null>(() => {
    if (!purchaseId) {
      return null;
    }
    return (
      data?.balanceHistory.find((purchase) => purchase._id === purchaseId) ??
      null
    );
  }, [purchaseId, data]);

  const [excelLoading, setExcelLoading] = useState(false);

  const generateExcel = useCallback(async () => {
    setExcelLoading(true);
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("BalanceHistory");

    worksheet.columns = [
      ...columns.map((column) => ({
        ...column,
        width: Math.max(12, column.header.length),
      })),
    ];
    data?.orderHistory?.forEach((item) => {
      const isIncome = item.type === OrderType.Income;

      const row = worksheet.addRow({
        beforeBalance: `${new Intl.NumberFormat().format(
          item.beforeBalance,
        )} ₮`,
        amount: `${isIncome ? "+" : "-"} ${new Intl.NumberFormat().format(
          item.amount,
        )} ₮`,
        afterBalance: `${new Intl.NumberFormat().format(item.afterBalance)} ₮`,
        createdAt: moment(item.createdAt).format("YYYY/MM/DD HH:mm:ss"),
        description: item.description,
        status: item.status,
      });

      const amountCell = row.getCell("amount");

      amountCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: isIncome ? "C6EFCE" : "FFC7CE" },
      };

      amountCell.font = {
        color: { argb: isIncome ? "006100" : "9C0006" },
        bold: true,
      };
    });
    const blob = new Blob([await workbook.xlsx.writeBuffer()], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    setExcelLoading(false);
    const url = window.URL.createObjectURL(blob);
    window.open(url, "_blank");
  }, [data?.orderHistory]);

  return (
    <>
      {error ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <img width="250" src={nodataImgUrl} alt="no-data" />
          <p>Мэдээлэл олдсонгүй</p>
        </div>
      ) : (
        <>
          <HistoryModal data={activePurchase} onClose={unset} />
          <div className="page-body">
            <Row justify="center">
              {loading ? (
                <FullLoading />
              ) : (
                <>
                  <Col span={6}>
                    <span className="question_title">Нийт хөрөнгийн дүн</span>
                    <p>
                      {formatBalance({ balance: data?.netAssetValue ?? 0 })} ₮
                    </p>
                  </Col>
                  {typeof data?.totalDeposit === "number" && (
                    <Col span={6}>
                      <span className="question_title">
                        Нийт цэнэглэсэн дүн
                      </span>
                      <p>{formatBalance({ balance: data.totalDeposit })} ₮</p>
                    </Col>
                  )}
                  <Col span={6}>
                    <span className="question_title">Нийт бэлэн мөнгө</span>
                    <p>
                      {formatBalance({
                        balance: Math.max(data?.cashAmount ?? 0, 0),
                      })}{" "}
                      ₮
                    </p>
                  </Col>
                  {(data?.cashAmount ?? 0) < 0 && (
                    <Col span={6}>
                      <span className="question_title">
                        Шимтгэлээс үүссэн авлага
                      </span>
                      <p style={{ color: "red" }}>
                        {formatBalance({
                          balance: Math.abs(data?.cashAmount ?? 0),
                        })}{" "}
                        ₮
                      </p>
                    </Col>
                  )}
                  <Col span={6}>
                    <span className="question_title">
                      Худалдан авалтаас үлдсэн дүн
                    </span>
                    <p>{formatBalance({ balance: data?.holdAmount ?? 0 })} ₮</p>
                  </Col>
                  <Col span={24}>
                    <Row>
                      <Divider orientation="left">
                        Захиалгын түүх{" "}
                        <Button onClick={generateExcel} loading={excelLoading}>
                          Эксел татах
                        </Button>
                      </Divider>
                    </Row>
                  </Col>
                  <Col span={24}>
                    <Table
                      pagination={false}
                      dataSource={data?.orderHistory?.reverse() ?? []}
                      loading={loading}
                      size="small"
                      bordered
                      columns={[
                        {
                          title: "Өмнөх үлдэгдэл",
                          dataIndex: "beforeBalance",
                          key: "beforeBalance",
                          render: (text) => (
                            <span>{formatBalance({ balance: text })} ₮</span>
                          ),
                        },
                        {
                          title: "Захиалгын дүн",
                          dataIndex: "amount",
                          key: "amount",
                          render: (text, record) => (
                            <span
                              style={{
                                color:
                                  record.type === OrderType.Income
                                    ? "green"
                                    : "red",
                              }}
                            >
                              {record.type === OrderType.Income ? "+" : "-"}{" "}
                              {formatBalance({ balance: text })} ₮
                            </span>
                          ),
                        },
                        {
                          title: "Дараах үлдэгдэл",
                          dataIndex: "afterBalance",
                          key: "afterBalance",
                          render: (text) => (
                            <span>{formatBalance({ balance: text })} ₮</span>
                          ),
                        },
                        {
                          title: "Огноо",
                          dataIndex: "createdAt",
                          key: "createdAt",
                          render: (text) => (
                            <span>
                              {moment(text).format("YYYY/MM/DD HH:mm:ss")}
                            </span>
                          ),
                        },
                        {
                          title: "Гүйлгээний утга",
                          dataIndex: "description",
                          key: "description",
                          render: (text) => <span>{text}</span>,
                        },
                        {
                          title: "Төлөв",
                          dataIndex: "status",
                          key: "status",
                        },
                      ]}
                    />
                  </Col>
                  <Col span={24}>
                    <Divider orientation="left">
                      Худалдан авалтын түүх
                      <Button
                        loading={historyLoading}
                        onClick={downloadBalanceExcel}
                      >
                        Эксел татах
                      </Button>
                    </Divider>
                  </Col>
                  <Col span={24}>
                    <Table
                      pagination={false}
                      dataSource={data?.balanceHistory ?? []}
                      loading={loading}
                      size="small"
                      bordered
                      columns={[
                        {
                          title: "Худалдан авалтын дүн",
                          dataIndex: "title",
                          key: "title",
                        },
                        {
                          title: "Огноо",
                          dataIndex: "createdAt",
                          key: "createdAt",
                          render: (text) => (
                            <span>
                              {moment(text).format("YYYY/MM/DD HH:mm:ss")}
                            </span>
                          ),
                        },
                        {
                          title: "Хөрөнгө оруулсан дүн",
                          dataIndex: "principleAmount",
                          key: "principleAmount",
                          render: (text) => (
                            <span>{formatBalance({ balance: text })} ₮</span>
                          ),
                        },
                        {
                          title: "Шимтгэл",
                          dataIndex: "fee",
                          key: "fee",
                          render: (text) => (
                            <span>{formatBalance({ balance: text })} ₮</span>
                          ),
                        },
                        {
                          title: "Худалдан авалтын үлдсэн дүн",
                          key: "holdAmount",
                          render: (_, record) => (
                            <span>
                              {formatBalance({
                                balance: record.holdAmount ?? 0,
                              })}{" "}
                              ₮
                            </span>
                          ),
                        },
                        {
                          title: "Чөлөөлөгдсөн дүн",
                          key: "releasedAmount",
                          render: (_, record) => (
                            <span>
                              {formatBalance({
                                balance: record.releasedAmount ?? 0,
                              })}{" "}
                              ₮
                            </span>
                          ),
                        },
                        {
                          width: 110,
                          dataIndex: "button",
                          key: "button",
                          render: (_, record) => (
                            <Row gutter={[8, 8]}>
                              <Col>
                                <Button
                                  type="primary"
                                  shape="circle"
                                  onClick={() => {
                                    invariant(
                                      record._id,
                                      "record._id must be defined",
                                    );
                                    set(record._id);
                                  }}
                                >
                                  <EyeOutlined />
                                </Button>
                              </Col>
                              {canEdit && (
                                <Col>
                                  {holdLoading &&
                                  currentPurchaseId === record._id ? (
                                    <LoadingOutlined />
                                  ) : (
                                    <Button
                                      type="default"
                                      shape="circle"
                                      onClick={() => {
                                        if (record._id) {
                                          releaseHold(record._id);
                                        }
                                      }}
                                    >
                                      <DeliveredProcedureOutlined />
                                    </Button>
                                  )}
                                </Col>
                              )}
                            </Row>
                          ),
                        },
                      ]}
                    />
                  </Col>
                </>
              )}
            </Row>
          </div>
        </>
      )}
    </>
  );
};
